import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import {
  CheckCircle, Clock, AlertTriangle, CreditCard,
  User, Package, Banknote, RefreshCw, ChevronDown, ChevronUp,
} from 'lucide-react';
import { stripeConnectApi, type PaymentVerificationItem } from '../services/api';

// ─── helpers (roleFrom kept only for the QueueRow component props) ────────────

const PAYMENT_STATUS: Record<number, { label: string; color: string }> = {
  1: { label: 'Pending',     color: 'bg-gray-100 text-gray-600' },
  2: { label: 'Processing',  color: 'bg-blue-100 text-blue-700' },
  3: { label: 'Released',    color: 'bg-green-100 text-green-700' },
  4: { label: 'Failed',      color: 'bg-red-100 text-red-700' },
  5: { label: 'Refunded',    color: 'bg-amber-100 text-amber-700' },
  6: { label: 'On Hold',     color: 'bg-orange-100 text-orange-700' },
  7: { label: 'Ops Approved', color: 'bg-indigo-100 text-indigo-700' },
};

const PAYOUT_METHOD: Record<number, string> = {
  1: 'Bank Transfer',
  2: 'Stripe',
};

function fmt(n: number) { return `£${n.toFixed(2)}`; }
function fmtDate(s: string | null) { return s ? new Date(s).toLocaleString('en-GB') : '—'; }

// ─── row component ─────────────────────────────────────────────────────────

function QueueRow({
  item, isAdmin, isOps, onOpsApprove, onRelease, onAdvanceToProcessing,
  approving, releasing, advancing,
}: {
  item: PaymentVerificationItem;
  isAdmin: boolean;
  isOps: boolean;
  onOpsApprove: () => void;
  onRelease: () => void;
  onAdvanceToProcessing: () => void;
  approving: boolean;
  releasing: boolean;
  advancing: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const status = PAYMENT_STATUS[item.paymentStatus] ?? { label: 'Unknown', color: 'bg-gray-100 text-gray-500' };
  const canOpsApprove          = (isOps || isAdmin) && item.paymentStatus === 2;
  const canRelease             = isAdmin && item.paymentStatus === 7;
  const canAdvanceToProcessing = (isOps || isAdmin) && item.paymentStatus === 1;

  return (
    <div
      data-testid={`payment-queue-item-${item.jobId}`}
      className="border border-gray-200 rounded-xl bg-white shadow-sm overflow-hidden"
    >
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-3">
        {/* POD thumbnail */}
        {item.podPhotoUrl ? (
          <a href={item.podPhotoUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
            <img
              src={item.podPhotoUrl}
              alt="POD"
              className="w-14 h-14 rounded-lg object-cover border border-gray-200 hover:opacity-80 transition-opacity"
            />
          </a>
        ) : (
          <div className="w-14 h-14 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400 border border-gray-200 shrink-0">
            <Package className="w-6 h-6" />
          </div>
        )}

        {/* Core info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm truncate">{item.jobTitle}</span>
            <span className="text-xs text-gray-400">{item.jobReferenceNumber}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${status.color}`}>
              {status.label}
            </span>
          </div>
          <div className="flex gap-4 mt-0.5 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1"><User className="w-3 h-3" /> Carrier: {item.carrierFullName}</span>
            <span className="flex items-center gap-1"><User className="w-3 h-3" /> Customer: {item.customerFullName}</span>
          </div>
        </div>

        {/* Amounts */}
        <div className="text-right shrink-0 hidden sm:block">
          <div className="text-sm font-bold text-gray-900">{fmt(item.netAmount)} <span className="text-xs font-normal text-gray-400">net</span></div>
          <div className="text-xs text-gray-400">{fmt(item.grossAmount)} gross</div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 shrink-0">
          {canAdvanceToProcessing && (
            <button
              onClick={onAdvanceToProcessing}
              disabled={advancing}
              title="Simulate Stripe payment confirmation — advances status from Pending to Processing"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {advancing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Confirm Payment
            </button>
          )}
          {canOpsApprove && (
            <button
              onClick={onOpsApprove}
              disabled={approving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {approving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
              Ops Approve
            </button>
          )}
          {canRelease && (
            <button
              onClick={onRelease}
              disabled={releasing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors"
            >
              {releasing ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Banknote className="w-3 h-3" />}
              Release
            </button>
          )}
          <button
            onClick={() => setExpanded(e => !e)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3 grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs text-gray-600 bg-gray-50">
          {/* Payment breakdown */}
          <div>
            <p className="font-semibold text-gray-700 mb-1 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Payment</p>
            <p>Gross: {fmt(item.grossAmount)}</p>
            <p>Commission: -{fmt(item.commissionDeducted)}</p>
            {item.cashbackDeducted > 0   && <p>Cashback: -{fmt(item.cashbackDeducted)}</p>}
            {item.referralDeducted > 0   && <p>Referral: -{fmt(item.referralDeducted)}</p>}
            <p className="font-semibold text-green-700 mt-1">Net: {fmt(item.netAmount)}</p>
            <p className="mt-1 text-gray-400">{PAYOUT_METHOD[item.payoutMethod] ?? '—'} · {item.jobPaymentTerm === 0 ? 'Cash' : 'Credit'}</p>
          </div>

          {/* Driver payout info */}
          <div>
            <p className="font-semibold text-gray-700 mb-1 flex items-center gap-1"><User className="w-3 h-3" /> Driver Payout</p>
            <p className="truncate">{item.carrierEmail}</p>
            {item.carrierStripeAccountId ? (
              <>
                <p className="text-indigo-600 truncate">{item.carrierStripeAccountId}</p>
                <p>{item.carrierStripeOnboarded ? '✅ Onboarded' : '⚠️ Not onboarded'}</p>
              </>
            ) : (
              <>
                {item.carrierSortCode    && <p>Sort code: {item.carrierSortCode}</p>}
                {item.carrierAccountNumber && <p>Account: {item.carrierAccountNumber}</p>}
                {!item.carrierSortCode && !item.carrierStripeAccountId && (
                  <p className="text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> No payout method
                  </p>
                )}
              </>
            )}
          </div>

          {/* Stripe / POD */}
          <div>
            <p className="font-semibold text-gray-700 mb-1 flex items-center gap-1"><Package className="w-3 h-3" /> Stripe / POD</p>
            {item.stripePaymentIntentId && (
              <p className="truncate text-gray-500">Intent: {item.stripePaymentIntentId}</p>
            )}
            {item.stripeTransferId && (
              <p className="truncate text-green-600">Transfer: {item.stripeTransferId}</p>
            )}
            {item.podRecipientName && <p>Recipient: {item.podRecipientName}</p>}
            {item.podCapturedAt    && <p>POD at: {fmtDate(item.podCapturedAt)}</p>}
            {item.podSignatureUrl  && (
              <a href={item.podSignatureUrl} target="_blank" rel="noopener noreferrer"
                 className="text-blue-600 underline">Signature</a>
            )}
          </div>

          {/* Timeline */}
          <div className="col-span-2 sm:col-span-3 border-t border-gray-200 pt-2 flex flex-wrap gap-4 text-gray-400">
            <span><Clock className="w-3 h-3 inline mr-0.5" />Created: {fmtDate(item.createdAt)}</span>
            {item.opsApprovedAt && <span>Ops Approved: {fmtDate(item.opsApprovedAt)}</span>}
            {item.releasedAt    && <span>Released: {fmtDate(item.releasedAt)}</span>}
            {item.failureReason && (
              <span className="text-red-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {item.failureReason}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── page ──────────────────────────────────────────────────────────────────

export default function PaymentVerificationQueuePage() {
  const { account, getToken } = useAppAuth();
  const { role, isAdminOrOps } = useRole();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const isAdmin = role === 'admin';
  const isOps   = role === 'ops' || role === 'operations';

  const { data: queue = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['payment-verification-queue', page],
    queryFn: async () => {
      const token = await getToken();
      return stripeConnectApi.getVerificationQueue(token, page, pageSize);
    },
    refetchInterval: 60_000,
  });

  const [actionId, setActionId] = useState<string | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'release' | 'advance' | null>(null);

  const approveMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      const token = await getToken();
      return stripeConnectApi.opsApprovePayment(paymentId, account?.userId ?? '', token);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-verification-queue'] });
      setActionId(null); setActionType(null);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      const token = await getToken();
      return stripeConnectApi.adminReleasePayment(paymentId, account?.userId ?? '', token);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-verification-queue'] });
      setActionId(null); setActionType(null);
    },
  });

  const advanceMutation = useMutation({
    mutationFn: async (paymentId: string) => {
      const token = await getToken();
      return stripeConnectApi.advancePaymentToProcessing(paymentId, token);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-verification-queue'] });
      setActionId(null); setActionType(null);
    },
  });

  if (!isAdminOrOps) {
    return (
      <div className="p-8 text-center text-gray-500">
        You do not have permission to view the payment verification queue.
      </div>
    );
  }

  return (
    <div className="page-shell">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Payment Verification Queue</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Cross-check POD evidence and release driver payments. Ops approve → Admin release.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Error / loading */}
      {isError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Failed to load queue. Check your connection and try refreshing.
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-16">
          <RefreshCw className="w-8 h-8 text-blue-400 animate-spin" />
        </div>
      )}

      {!isLoading && queue.length === 0 && (
        <div className="py-16 text-center text-gray-400">
          <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
          <p className="font-semibold">Queue is clear</p>
          <p className="text-sm mt-1">No payments awaiting verification.</p>
        </div>
      )}

      {/* Queue items */}
      <div className="flex flex-col gap-3">
        {queue.map(item => (
          <QueueRow
            key={item.paymentId}
            item={item}
            isAdmin={isAdmin}
            isOps={isOps}
            approving={actionId === item.paymentId && actionType === 'approve' && approveMutation.isPending}
            releasing={actionId === item.paymentId && actionType === 'release' && releaseMutation.isPending}
            advancing={actionId === item.paymentId && actionType === 'advance' && advanceMutation.isPending}
            onOpsApprove={() => {
              setActionId(item.paymentId);
              setActionType('approve');
              approveMutation.mutate(item.paymentId);
            }}
            onRelease={() => {
              setActionId(item.paymentId);
              setActionType('release');
              releaseMutation.mutate(item.paymentId);
            }}
            onAdvanceToProcessing={() => {
              setActionId(item.paymentId);
              setActionType('advance');
              advanceMutation.mutate(item.paymentId);
            }}
          />
        ))}
      </div>

      {/* Pagination */}
      {queue.length === pageSize && (
        <div className="flex justify-center gap-2 mt-6">
          {page > 1 && (
            <button
              onClick={() => setPage(p => p - 1)}
              className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Previous
            </button>
          )}
          <button
            onClick={() => setPage(p => p + 1)}
            className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
